/** Restores the autosaved working state on startup and keeps autosaving afterwards. */
import { t } from '../i18n/i18n';
import { type DecodedImage, decodeStoredImage } from '../imaging/decode';
import { type ImageData8, loadSession } from '../project/db';
import { Autosave } from '../state/autosave';
import { type Store, store as defaultStore } from '../state/store';
import { setImage } from './actions';

export const autosave = new Autosave(defaultStore);

/**
 * Restores the last session (design, image, project name, view). Returns true when something
 * was restored. Never throws: a broken session is discarded so the app always starts.
 */
export async function restoreSession(
  s: Store = defaultStore,
  decode: (img: ImageData8) => Promise<DecodedImage> = (img) => decodeStoredImage(img.bytes, img.mime, img.name),
): Promise<boolean> {
  try {
    const session = await loadSession();
    if (!session) return false;
    // the user may already have started working while the session was loading
    if (s.design.objects.length || s.getState().image) return false;
    let decoded: DecodedImage | null = null;
    if (session.image) {
      try {
        decoded = await decode(session.image);
      } catch {
        decoded = null;
      }
    }
    if (decoded) setImage(decoded, s);
    const p = session.record.project;
    s.resetDesign(session.file.design, { id: p.id, name: p.name, createdAt: p.createdAt, lastSaved: p.lastSaved, dirty: p.dirty, cloud: p.cloud === true, folder: session.file.folder ?? null });
    if (session.record.view) s.set({ viewport: session.record.view });
    else s.set((st) => ({ fitNonce: st.fitNonce + 1 }));
    return session.file.design.objects.length > 0 || !!decoded;
  } catch {
    return false;
  }
}

/** Startup: restore first, then start autosaving (so the empty initial state never overwrites the session). */
export async function startSession(s: Store = defaultStore, a: Autosave = autosave): Promise<boolean> {
  const restored = await restoreSession(s);
  a.onError = (msg) => s.toast('error', t('Autosave failed: {msg}. Save the project manually or download the project file.', { msg }), 0);
  a.start();
  // write immediately when the tab is hidden or closed (most reliable moment on mobile/tablets)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && a.pending) void a.flush();
    });
  }
  if (restored) s.toast('info', t('Your work has been restored from your last session.'));
  return restored;
}
